import { Grid } from '../core/Grid';
import { Cell, Color, TRANSPARENT } from '../core/types';
import { BRAILLE_BIT, BRAILLE_COLS, BRAILLE_ROWS, brailleFromMask } from '../unicode/braille';
import { charForEdges, Weight } from '../unicode/boxdrawing';
import { halfBlockChar, quadrantChar, shadeFor } from '../unicode/blocks';

/**
 * Text and logo generator.
 *
 * The idea is one step removed from "type letters into cells": a shape — text
 * rendered in a real font, or an imported logo — becomes a *bitmap*, and the
 * bitmap is then expressed in whichever character family you choose. Braille
 * gives 2x4 sub-cell resolution, so letterforms keep their curves; box drawing
 * turns strokes into ═ ║ ╔ ╗ joints; blocks and shading give solid poster text.
 *
 * `knockout` mode is the interesting one: the rectangle is filled solid and the
 * shape is *removed* from it, so the text reads as holes punched in a slab.
 *
 * Everything here is pure. Rasterising a font needs a canvas, so that lives in
 * `textRaster.ts` and is injected — which is also what makes this testable.
 */

export type FillStyle =
  | 'braille'
  | 'quarter'
  | 'halfblock'
  | 'block'
  | 'shade'
  | 'box-light'
  | 'box-heavy'
  | 'box-double'
  | 'custom';

export type RenderMode = 'solid' | 'knockout' | 'outline';

export const FILL_LABELS: Record<FillStyle, string> = {
  braille: 'Braille (2×4 dots)',
  quarter: 'Quadrant blocks',
  halfblock: 'Half blocks',
  block: 'Full blocks',
  shade: 'Shading',
  'box-light': 'Box drawing — light',
  'box-heavy': 'Box drawing — heavy',
  'box-double': 'Box drawing — double',
  custom: 'Single character',
};

export const MODE_LABELS: Record<RenderMode, string> = {
  solid: 'Solid — the shape is drawn',
  knockout: 'Knockout — the shape is cut out of a filled block',
  outline: 'Outline — only the edge of the shape',
};

/** A 1-bit bitmap in sub-cell units. */
export interface BitMask {
  width: number;
  height: number;
  data: Uint8Array;
}

export function makeMask(width: number, height: number): BitMask {
  return { width: Math.max(0, width), height: Math.max(0, height), data: new Uint8Array(Math.max(0, width * height)) };
}

export function maskGet(mask: BitMask, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= mask.width || y >= mask.height) return false;
  return mask.data[y * mask.width + x] === 1;
}

export function maskSet(mask: BitMask, x: number, y: number, on: boolean): void {
  if (x < 0 || y < 0 || x >= mask.width || y >= mask.height) return;
  mask.data[y * mask.width + x] = on ? 1 : 0;
}

/**
 * A sensible text height for each fill style, in terminal rows.
 *
 * Legibility is governed by the *dot* height, not the row count: Braille packs
 * four dots into a row, while a box-drawing cell is a single stroke. Five rows
 * of Braille is a comfortable capital height; five rows of box drawing is an
 * unreadable smudge. These are the row counts that land in the same place.
 */
export const SUGGESTED_ROWS: Record<FillStyle, number> = {
  braille: 5,
  quarter: 8,
  halfblock: 8,
  block: 10,
  shade: 10,
  'box-light': 12,
  'box-heavy': 12,
  'box-double': 12,
  custom: 10,
};

/** Sub-cell resolution each fill style works at. */
export function dotsPerCell(fill: FillStyle): { x: number; y: number } {
  switch (fill) {
    case 'braille':
      return { x: BRAILLE_COLS, y: BRAILLE_ROWS };
    case 'quarter':
      return { x: 2, y: 2 };
    case 'halfblock':
      return { x: 1, y: 2 };
    default:
      return { x: 1, y: 1 };
  }
}

/** Tightest box containing the set bits, or null when the mask is empty. */
export function maskBounds(mask: BitMask): { x: number; y: number; w: number; h: number } | null {
  let minX = mask.width;
  let minY = mask.height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < mask.height; y++) {
    for (let x = 0; x < mask.width; x++) {
      if (mask.data[y * mask.width + x] === 0) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return maxX < 0 ? null : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Crops to the set bits, leaving `pad` dots of margin. */
export function trimMask(mask: BitMask, pad = 0): BitMask {
  const b = maskBounds(mask);
  if (!b) return makeMask(0, 0);
  const out = makeMask(b.w + pad * 2, b.h + pad * 2);
  for (let y = 0; y < b.h; y++) {
    for (let x = 0; x < b.w; x++) {
      if (maskGet(mask, b.x + x, b.y + y)) maskSet(out, x + pad, y + pad, true);
    }
  }
  return out;
}

/** Bits set in `a` but not in `b`. Used to punch a shape out of a slab. */
export function subtractMask(a: BitMask, b: BitMask, offsetX = 0, offsetY = 0): BitMask {
  const out = makeMask(a.width, a.height);
  for (let y = 0; y < a.height; y++) {
    for (let x = 0; x < a.width; x++) {
      if (!maskGet(a, x, y)) continue;
      if (maskGet(b, x - offsetX, y - offsetY)) continue;
      maskSet(out, x, y, true);
    }
  }
  return out;
}

/** Bits on the boundary of the shape: set, but touching something unset. */
export function outlineMask(mask: BitMask, thickness = 1): BitMask {
  const out = makeMask(mask.width, mask.height);
  for (let y = 0; y < mask.height; y++) {
    for (let x = 0; x < mask.width; x++) {
      if (!maskGet(mask, x, y)) continue;
      let edge = false;
      for (let d = 1; d <= thickness && !edge; d++) {
        edge =
          !maskGet(mask, x - d, y) ||
          !maskGet(mask, x + d, y) ||
          !maskGet(mask, x, y - d) ||
          !maskGet(mask, x, y + d);
      }
      if (edge) maskSet(out, x, y, true);
    }
  }
  return out;
}

export function filledMask(width: number, height: number): BitMask {
  const mask = makeMask(width, height);
  mask.data.fill(1);
  return mask;
}

/**
 * Turns every diagonal step into an orthogonal one.
 *
 * Box-drawing characters can only join along edges, so two cells that touch at
 * a corner cannot be linked — and a letterform's slants and curves are made of
 * exactly those. Filling one of the two cells they share converts each diagonal
 * into an L, after which ordinary edge connectivity draws a clean staircase.
 *
 * Doing this on the bitmap rather than per character matters: the previous
 * attempt inferred edges from diagonal neighbours directly, and since a contour
 * cell usually has two of them, cells collected four edges and became ╬ in the
 * middle of a smooth curve.
 */
/**
 * Cells reachable from outside the shape, walking only through empty cells.
 *
 * Used to tell the outside of a letter from the hole inside it — the counter of
 * an O or the bowl of a P.
 */
function exteriorCells(mask: BitMask): Uint8Array {
  const seen = new Uint8Array(mask.width * mask.height);
  const stack: number[] = [];
  const push = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= mask.width || y >= mask.height) return;
    const i = y * mask.width + x;
    if (seen[i] || mask.data[i] === 1) return;
    seen[i] = 1;
    stack.push(i);
  };

  for (let x = 0; x < mask.width; x++) {
    push(x, 0);
    push(x, mask.height - 1);
  }
  for (let y = 0; y < mask.height; y++) {
    push(0, y);
    push(mask.width - 1, y);
  }

  while (stack.length) {
    const i = stack.pop() as number;
    const x = i % mask.width;
    const y = (i - x) / mask.width;
    push(x - 1, y);
    push(x + 1, y);
    push(x, y - 1);
    push(x, y + 1);
  }
  return seen;
}

export function connectDiagonals(mask: BitMask): BitMask {
  const out = makeMask(mask.width, mask.height);
  out.data.set(mask.data);
  const exterior = exteriorCells(mask);
  const isOutside = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < mask.width && y < mask.height && exterior[y * mask.width + x] === 1;

  for (let y = 0; y < mask.height; y++) {
    for (let x = 0; x < mask.width; x++) {
      if (!maskGet(mask, x, y)) continue;
      for (const [dx, dy] of [
        [1, -1],
        [1, 1],
      ] as const) {
        const nx = x + dx;
        const ny = y + dy;
        if (!maskGet(mask, nx, ny)) continue;
        // Already connected through a shared edge neighbour: nothing to do.
        if (maskGet(mask, nx, y) || maskGet(mask, x, ny)) continue;

        /*
         * Square the corner off on the *outside* of the letter.
         *
         * Both shared cells would join the two strokes, but one of them sits
         * inside the counter — the hole in an O or a P — and filling that eats
         * into it. Worse, which one the loop reached first depended on the scan
         * direction, so the left corner of an O ended up stepped inward while
         * the right one stepped outward, and the letter came out lopsided.
         */
        const horizontal = { x: nx, y };
        const vertical = { x, y: ny };
        const pick =
          isOutside(horizontal.x, horizontal.y) === isOutside(vertical.x, vertical.y)
            ? horizontal
            : isOutside(horizontal.x, horizontal.y)
              ? horizontal
              : vertical;
        maskSet(out, pick.x, pick.y, true);
      }
    }
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Bitmap -> characters
 * ------------------------------------------------------------------ */

const BOX_WEIGHT: Record<string, Weight> = {
  'box-light': 1,
  'box-heavy': 2,
  'box-double': 3,
};

const BOX_FALLBACK: Record<string, string> = {
  'box-light': '─',
  'box-heavy': '━',
  'box-double': '═',
};

export interface CellsOptions {
  fill: FillStyle;
  /** Character used by the `custom` fill. */
  customChar?: string;
  /** Cell size of the output; defaults to the mask's own size. */
  cols?: number;
  rows?: number;
}

/**
 * Converts a sub-cell bitmap into characters.
 *
 * Each fill style reads the dots belonging to a cell and names them: Braille
 * encodes all eight, quadrants and half blocks encode two or four, shading
 * reports how full the cell is, and the box styles look at the *neighbouring
 * cells* instead — a stroke running left to right becomes ═, a corner becomes
 * ╔, because that is what the connectivity says.
 */
export function maskToChars(
  sourceMask: BitMask,
  options: CellsOptions,
): Array<{ x: number; y: number; char: string }> {
  const dots = dotsPerCell(options.fill);
  // Box characters join along edges only, so diagonal steps are squared off
  // first; every other family reads its own cell and needs no such help.
  const mask = options.fill.startsWith('box-') ? connectDiagonals(sourceMask) : sourceMask;
  const cols = options.cols ?? Math.ceil(mask.width / dots.x);
  const rows = options.rows ?? Math.ceil(mask.height / dots.y);
  const out: Array<{ x: number; y: number; char: string }> = [];

  const cellOn = (cx: number, cy: number): boolean => {
    for (let dy = 0; dy < dots.y; dy++) {
      for (let dx = 0; dx < dots.x; dx++) {
        if (maskGet(mask, cx * dots.x + dx, cy * dots.y + dy)) return true;
      }
    }
    return false;
  };

  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      let char = '';

      switch (options.fill) {
        case 'braille': {
          let bits = 0;
          for (let r = 0; r < BRAILLE_ROWS; r++) {
            for (let c = 0; c < BRAILLE_COLS; c++) {
              if (maskGet(mask, cx * BRAILLE_COLS + c, cy * BRAILLE_ROWS + r)) bits |= BRAILLE_BIT[r][c];
            }
          }
          if (bits !== 0) char = brailleFromMask(bits);
          break;
        }
        case 'quarter': {
          const tl = maskGet(mask, cx * 2, cy * 2);
          const tr = maskGet(mask, cx * 2 + 1, cy * 2);
          const bl = maskGet(mask, cx * 2, cy * 2 + 1);
          const br = maskGet(mask, cx * 2 + 1, cy * 2 + 1);
          const q = quadrantChar(tl, tr, bl, br);
          if (q !== ' ') char = q;
          break;
        }
        case 'halfblock': {
          const top = maskGet(mask, cx, cy * 2);
          const bottom = maskGet(mask, cx, cy * 2 + 1);
          const h = halfBlockChar(top, bottom);
          if (h !== ' ') char = h;
          break;
        }
        case 'block': {
          if (cellOn(cx, cy)) char = '█';
          break;
        }
        case 'shade': {
          // Shading needs a density, so it counts dots rather than testing one.
          let on = 0;
          let total = 0;
          for (let dy = 0; dy < dots.y; dy++) {
            for (let dx = 0; dx < dots.x; dx++) {
              total += 1;
              if (maskGet(mask, cx * dots.x + dx, cy * dots.y + dy)) on += 1;
            }
          }
          const s = shadeFor(total === 0 ? 0 : on / total);
          if (s !== ' ') char = s;
          break;
        }
        case 'box-light':
        case 'box-heavy':
        case 'box-double': {
          if (!cellOn(cx, cy)) break;
          const weight = BOX_WEIGHT[options.fill];
          const edges = {
            n: cellOn(cx, cy - 1) ? weight : 0,
            e: cellOn(cx + 1, cy) ? weight : 0,
            s: cellOn(cx, cy + 1) ? weight : 0,
            w: cellOn(cx - 1, cy) ? weight : 0,
          };

          /*
           * Extend the ends of a stroke.
           *
           * A cell with a single neighbour would otherwise ask for a half-line,
           * which Unicode only provides in light weight — so the end of a heavy
           * or double stroke degraded into a thin stub. Mirroring the edge makes
           * it a full segment, which is what the stroke looks like anyway.
           */
          const live = (['n', 'e', 's', 'w'] as const).filter((d) => edges[d] !== 0);
          if (live.length === 1) {
            const opposite = { n: 's', e: 'w', s: 'n', w: 'e' } as const;
            edges[opposite[live[0]]] = weight;
          } else if (live.length === 0) {
            // An isolated cell: a short horizontal segment reads best.
            edges.e = weight;
            edges.w = weight;
          }

          char =
            charForEdges(edges as { n: Weight; e: Weight; s: Weight; w: Weight }) ??
            BOX_FALLBACK[options.fill];
          break;
        }
        case 'custom': {
          if (cellOn(cx, cy)) char = options.customChar || '█';
          break;
        }
      }

      if (char !== '') out.push({ x: cx, y: cy, char });
    }
  }

  return out;
}

/* ------------------------------------------------------------------ *
 * Composition
 * ------------------------------------------------------------------ */

export interface GenerateOptions {
  mode: RenderMode;
  fill: FillStyle;
  customChar?: string;
  /** Cells of margin around the shape (knockout: size of the slab border). */
  padX?: number;
  padY?: number;
  outlineThickness?: number;
  /** Colour for a cell, in cell coordinates. Lets callers pass a gradient. */
  colorAt?: (x: number, y: number, cols: number, rows: number) => { fg: Color; bg: Color };
  attrs?: number;
}

export interface GenerateResult {
  grid: Grid;
  cols: number;
  rows: number;
  /** How many cells carry ink. */
  inkCells: number;
}

/**
 * Turns a shape bitmap into a grid of characters.
 *
 * The shape arrives already rasterised at the fill style's resolution, so this
 * only has to decide what the mask means: draw it, punch it out of a slab, or
 * trace its edge.
 */
export function generateFromMask(shape: BitMask, options: GenerateOptions): GenerateResult {
  const dots = dotsPerCell(options.fill);
  const padX = Math.max(0, Math.round(options.padX ?? 0));
  const padY = Math.max(0, Math.round(options.padY ?? 0));

  const trimmed = trimMask(shape);
  if (trimmed.width === 0 || trimmed.height === 0) {
    return { grid: new Grid(1, 1), cols: 0, rows: 0, inkCells: 0 };
  }

  // Grow to whole cells, then add the margin in cell units.
  const shapeCols = Math.ceil(trimmed.width / dots.x);
  const shapeRows = Math.ceil(trimmed.height / dots.y);
  const cols = shapeCols + padX * 2;
  const rows = shapeRows + padY * 2;

  const canvas = makeMask(cols * dots.x, rows * dots.y);
  const offsetX = padX * dots.x;
  const offsetY = padY * dots.y;

  let working: BitMask;
  switch (options.mode) {
    case 'knockout': {
      // Fill everything, then remove the shape — the text becomes the hole.
      const slab = filledMask(canvas.width, canvas.height);
      working = subtractMask(slab, trimmed, offsetX, offsetY);
      break;
    }
    case 'outline': {
      const edge = outlineMask(trimmed, Math.max(1, options.outlineThickness ?? 1));
      working = canvas;
      for (let y = 0; y < edge.height; y++) {
        for (let x = 0; x < edge.width; x++) {
          if (maskGet(edge, x, y)) maskSet(working, x + offsetX, y + offsetY, true);
        }
      }
      break;
    }
    default: {
      working = canvas;
      for (let y = 0; y < trimmed.height; y++) {
        for (let x = 0; x < trimmed.width; x++) {
          if (maskGet(trimmed, x, y)) maskSet(working, x + offsetX, y + offsetY, true);
        }
      }
      break;
    }
  }

  const chars = maskToChars(working, { fill: options.fill, customChar: options.customChar, cols, rows });
  const grid = new Grid(Math.max(1, cols), Math.max(1, rows));

  for (const { x, y, char } of chars) {
    const colors = options.colorAt?.(x, y, cols, rows);
    const cell: Cell = {
      char,
      fg: colors?.fg ?? TRANSPARENT,
      bg: colors?.bg ?? TRANSPARENT,
      attrs: options.attrs ?? 0,
    };
    grid.set(x, y, cell);
  }

  return { grid, cols, rows, inkCells: chars.length };
}

/** Plain-text preview of a generated grid. */
export function previewText(result: GenerateResult): string {
  return result.grid.toText();
}
